import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgxUiLoaderService } from 'ngx-ui-loader';

import { CurrencyPipe } from '@angular/common';
import { ChartOptions, ChartType, ChartDataset, Chart, TooltipModel, ChartTypeRegistry } from 'chart.js';
import { FinancialDashboardData } from '../../_models/financialDashboardData';
import { FilterOption } from 'app/_componentes/header/_models/filterOption';
import { Globals } from 'app/_models/globals';

@Component({
    selector: 'app-financial-dashboard-data', templateUrl: './financial-dashboard-data.component.html', styleUrls: ['./financial-dashboard-data.component.scss'],
    standalone: false
})
export class SupportFinancialDashboardDataComponent {
    @Input() public sectionTitle: string;
    @Input() public hasGraph: boolean = true;
    @Input() public hasTable: boolean = true;
    @Input() public dashboardData: FinancialDashboardData;
    @Input() public hasWorkingDaysCount: boolean = true;
    @Input() public hasBestMonth: boolean = true;
    @Input() public isCashback: boolean = false;
    @Input() public isCommerceGraph: boolean = false;

    @Input() public loaderName: string = '';
    @Input() public filterOptions?: FilterOption[] = [];
    @Input() public filterInput: string = '0';

    @Output() public onFilter: EventEmitter<any> = new EventEmitter<any>();
    @Output() public onReload: EventEmitter<any> = new EventEmitter<any>();

    public isError: boolean = false;
    public isEmpty: boolean = false;
    public isLoading: boolean = false;
    public barChartOptions: ChartOptions;
    public barChartType: ChartType = 'bar';
    public barChartLegend = true;
    public barChartData: ChartDataset[];
    public barChartLabels: string[];

    graphMaxValue: number = 0;

    constructor(private globals: Globals, private ngxLoader: NgxUiLoaderService) { }

    completeLoad(dashboardData?: FinancialDashboardData) {
        this.isLoading = false;
        this.isEmpty = false;

        if (!dashboardData) {
            this.isError = true;
            return;
        }

        if (dashboardData) {
            if (dashboardData.graphData.isEmpty) {
                this.isEmpty = true;
                return;
            }

            this.barChartLabels = dashboardData.graphData.labels;
            const dataMaxValue = Math.max(
                ...dashboardData.graphData.data[0].map((_, index) =>
                    dashboardData.graphData.data.reduce((sum, arr) => sum + arr[index], 0)
                )
            );

            var steps = this.getDynamicStep(dataMaxValue);
            this.graphMaxValue = Math.ceil(dataMaxValue / steps) * steps;

            if (this.isCommerceGraph) {
                this.barChartType = 'line';
                this.barChartData = [
                    { data: dashboardData.graphData.data[0], label: this.globals.translate('support.dashboard.dashboard-data.graph.subtitle.transacionado-total'), barThickness: 30, backgroundColor: '#3A032D', borderColor: '#3A032D', pointBackgroundColor: '#3A032D', pointBorderColor: '#3A032D' },
                ];
            } else if (this.isCashback) {
                this.barChartData = [
                    { data: dashboardData.graphData.data[0], label: this.globals.translate('support.dashboard.dashboard-data.graph.subtitle.repairer'), barThickness: 30, backgroundColor: '#3A032D', stack: 'a' },
                    { data: dashboardData.graphData.data[1], label: this.globals.translate('support.dashboard.dashboard-data.graph.subtitle.supplier'), barThickness: 30, backgroundColor: '#FE5D15', stack: 'a' },
                    { data: dashboardData.graphData.data[2], label: this.globals.translate('support.dashboard.dashboard-data.graph.subtitle.partsfy'), barThickness: 30, backgroundColor: '#C1EB43', stack: 'a' },
                ];
            } else {
                this.barChartData = [
                    { data: dashboardData.graphData.data[0], label: this.globals.translate('support.dashboard.dashboard-data.graph.subtitle.billable'), barThickness: 30, backgroundColor: '#3A032D', stack: 'a' },
                    { data: dashboardData.graphData.data[1], label: this.globals.translate('support.dashboard.dashboard-data.graph.subtitle.non-billable'), barThickness: 30, backgroundColor: '#FE5D15', stack: 'a' },
                ];
            }

            this.formatGraph();
        }

        this.ngxLoader.stopLoader('loader-' + this.loaderName);
    }

    filterControl(value) {
        this.onFilter.emit(value)
    }

    formatGraph() {
        this.barChartOptions = {
            elements: {
              line: {
                cubicInterpolationMode: 'monotone',
              },
            },
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                y: {
                    min: 0,
                    max: this.graphMaxValue,
                    stacked: true,
                    ticks: {
                        stepSize: this.graphMaxValue / 5,
                        callback: this.getTickCallback(this.graphMaxValue)
                    }
                }
            },
            plugins: {
                tooltip: {
                    enabled: false,
                    position: 'nearest',
                    external: this.externalTooltipHandler
                },
                legend: {
                    position: 'bottom',
                    labels: {
                        usePointStyle: true
                    }
                }
            }
        };
    }

    getDynamicStep(valor) {
        const magnitude = Math.pow(10, Math.floor(Math.log10(valor)));  // Ex: 1000 para 8345
        const step = Math.ceil(valor / magnitude) * magnitude / 2;      // Usa o valor médio entre 10, 50, 100...

        return Math.max(step, magnitude);  // Garante que o step seja sempre pelo menos a magnitude do valor
    }

    getTickCallback(graphMaxValue: number) {
        return (value) => {
            const val = typeof value === 'string' ? parseFloat(value) : value;

            if (val == 0) return '0';

            if (graphMaxValue < 1000)
                return `${val}`;

            if (graphMaxValue < 1000000)
                return `${(val / 1000).toFixed(1)}K`;

            return `${(val / 1000000).toFixed(1)}M`;
        };
    }

    getToopltipComplement(datasetIndex: number): string {
        if (this.isCashback) {
            switch (datasetIndex) {
                case 0:
                    return this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.body.repairer');
                case 1:
                    return this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.body.supplier');
                case 2:
                    return this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.body.partsfy');
                default:
                    return '';
            }
        }

        switch (datasetIndex) {
            case 0:
                return this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.body.billable');
            case 1:
                return this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.body.non-billable');
            default:
                return '';
        }
    }

    reloadControl() {
        this.onReload.emit(this.filterInput);
    }

    startLoader() {
        this.isLoading = true;
        this.isError = false;
        this.ngxLoader.startLoader('loader-' + this.loaderName);
    }

    get filter() {
        return this.filterInput;
    }

    set filter(value) {
        this.filterInput = value;
        this.filterControl(value);
    }

    public externalTooltipHandler = (context: { chart: Chart; tooltip: TooltipModel<keyof ChartTypeRegistry>; }) => {
        const {
            chart,
            tooltip
        } = context;
        const tooltipEl = this.getOrCreateTooltip(chart);

        // Esconde o tooltip personalizado se não houver tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
        }

        const dataPoint = tooltip.dataPoints[0];
        const dataset = dataPoint.dataset;
        const datasetIndex = dataPoint.datasetIndex;
        const dataIndex = dataPoint.dataIndex;
        const tooltipValue = dataPoint.raw as number;
        const isBestMonth =  this.hasBestMonth && dataset.data.length - 1 == dataIndex;

        // Soma total da coluna
        let columnTotal = 0;
        chart.data.datasets.forEach(ds => {
            const val = ds.data[dataIndex];
            columnTotal += typeof val === 'number' ? val : 0;
        });

        const tableRoot = tooltipEl.querySelector('table');

        // Remove todos os filhos do elemento table
        if (tableRoot && tableRoot.firstChild) {
            while (tableRoot.firstChild) {
                tableRoot.firstChild.remove();
            }
        }

        var titleDiv = this.handleTooltipHeaderCreation(columnTotal, tooltipValue, isBestMonth, tooltip);
        tableRoot.appendChild(titleDiv);

        if (!this.isCommerceGraph) {
            var bodyDiv = this.handleTooltipBodyCreation(columnTotal, tooltipValue, datasetIndex);
            tableRoot.appendChild(bodyDiv);
        }

        const {
            offsetLeft: positionX,
            offsetTop: positionY
        } = chart.canvas;

        tooltipEl.style.opacity = 1;

        const tooltipWidth = 225;
        tooltipEl.style.width = tooltipWidth + 'px';
        tooltipEl.style.left = this.handlePositionYCalculation(tooltip, tooltipWidth, positionX) + 'px';
        tooltipEl.style.top = positionY + tooltip.caretY + 'px';
    };

    private getOrCreateTooltip(chart) {
        let tooltipEl = chart.canvas.parentNode.querySelector('div');

        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.classList.add('tooltip-chartjs');
            const table = document.createElement('table');

            tooltipEl.appendChild(table);
            chart.canvas.parentNode.appendChild(tooltipEl);
        }

        return tooltipEl;
    };

    private handleTooltipHeaderCreation(columnTotal: number, selectedValue: number, isBestMonth: boolean, tooltip: TooltipModel<keyof ChartTypeRegistry>): HTMLDivElement {
        const percent = columnTotal > 0 ? ((selectedValue / columnTotal) * 100).toFixed(1) : '0';

        const titleDiv = document.createElement('div');
        titleDiv.classList.add('tooltip-title');

        const defaulSection = document.createElement('div');
        defaulSection.classList.add('default-section');

        const colorIcon = document.createElement('span');
        colorIcon.classList.add('color-icon');

        const colors = tooltip.labelColors[0];
        colorIcon.style.background = colors.backgroundColor.toString();
        colorIcon.style.borderColor = colors.borderColor.toString();

        const span = document.createElement('span');
        const title = this.isCommerceGraph ? `${new CurrencyPipe('pt-BR').transform(selectedValue, 'BRL')} ${this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.title.parts-commerce')}` : this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.title.default').replace('{{percentage}}', percent);
        const text = document.createTextNode(title);
        span.appendChild(text);

        defaulSection.appendChild(colorIcon);
        defaulSection.appendChild(span);
        titleDiv.appendChild(defaulSection);

        if (isBestMonth) {
            const bestMonthIcon = document.createElement('i');
            bestMonthIcon.classList.add("fa", "fa-star");
            titleDiv.appendChild(bestMonthIcon);
        }

        return titleDiv;
    }

    private handleTooltipBodyCreation(columnTotal: number, selectedValue: number, datasetIndex: number): HTMLDivElement {
        const bodyDiv = document.createElement('div');
        bodyDiv.classList.add('tooltip-body');

        const complement = this.getToopltipComplement(datasetIndex);
        const tooltipInfoSpan = document.createElement('span');
        tooltipInfoSpan.classList.add('tooltip-info');
        tooltipInfoSpan.innerText = `${complement} ${new CurrencyPipe('pt-BR').transform(selectedValue, 'BRL')}`;

        const columnInfoSpan = document.createElement('span');
        columnInfoSpan.classList.add('column-info');
        columnInfoSpan.innerText = `${this.globals.translate('support.dashboard.dashboard-data.graph.tooltip.body.total')} ${new CurrencyPipe('pt-BR').transform(columnTotal, 'BRL')}`;

        bodyDiv.appendChild(tooltipInfoSpan);
        bodyDiv.appendChild(columnInfoSpan);
        return bodyDiv;
    }

    private handlePositionYCalculation(tooltip: TooltipModel<keyof ChartTypeRegistry>, tooltipWidth: number, positionX: number): number {
        const screenLimit = 1280;
        const left = (tooltip.caretX + positionX) + ((tooltipWidth / 2) + 25);
        const shouldFlip = (left + tooltipWidth > screenLimit);

        if (shouldFlip)
            return positionX + tooltip.caretX - ((tooltipWidth / 2) + 25);

        return left;
    }
}
